<script setup>
import {storeToRefs} from 'pinia';
import {ref} from 'vue';
import {useAuthStore} from '@/stores/auth.store.js';
import {authClient} from '@/lib/auth.js';

const authStore = useAuthStore();
let {user} = storeToRefs(authStore);

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const revokeOtherSessions = ref(true);
const passwordError = ref('');
const passwordSuccess = ref('');
const submitting = ref(false);
const showGenerated = ref(false);

function generatePassword(length = 20) {
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!@#$%^&*-_=+?';
  const all = lower + upper + digits + symbols;

  const pick = (set) => set[crypto.getRandomValues(new Uint32Array(1))[0] % set.length];

  const chars = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  const remaining = new Uint32Array(length - chars.length);
  crypto.getRandomValues(remaining);
  for (const n of remaining) chars.push(all[n % all.length]);

  for (let i = chars.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function fillGeneratedPassword() {
  const pw = generatePassword();
  newPassword.value = pw;
  confirmPassword.value = pw;
  showGenerated.value = true;
  passwordError.value = '';
  passwordSuccess.value = '';
}

async function copyGenerated() {
  if (!newPassword.value) return;
  try {
    await navigator.clipboard.writeText(newPassword.value);
    passwordSuccess.value = 'Password copied to clipboard.';
  } catch {
    passwordError.value = 'Could not copy to clipboard.';
  }
}

async function changePassword() {
  passwordError.value = '';
  passwordSuccess.value = '';

  if (!currentPassword.value || !newPassword.value) {
    passwordError.value = 'Please fill in all fields.';
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    passwordError.value = 'New passwords do not match.';
    return;
  }
  if (newPassword.value.length < 8) {
    passwordError.value = 'New password must be at least 8 characters.';
    return;
  }

  submitting.value = true;
  const res = await authClient.changePassword({
    currentPassword: currentPassword.value,
    newPassword: newPassword.value,
    revokeOtherSessions: revokeOtherSessions.value,
  });
  submitting.value = false;

  if (res.error) {
    passwordError.value = res.error.message || 'Could not update password.';
    return;
  }

  passwordSuccess.value = 'Password updated.';
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
}
</script>


<template>
  <h1>Account settings</h1>

  <div class="row d-flex justify-content-center">
    <div class="col-lg-8 col-12">

      <div class="card mt-4">
        <div class="card-body">
          <h5 class="card-title">Profile Settings</h5>
          <div class="row my-3">
            <div class="col-12 px-3">
              <div class="input-group">
                <span class="input-group-text" id="basic-addon-email">Email</span>
                <input type="text" class="form-control" placeholder="Email" aria-label="Email"
                       aria-describedby="basic-addon-email" :value=user.email readonly>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card mt-4">
        <div class="card-body">
          <h5 class="card-title">Change password</h5>

          <div v-if="passwordError" class="alert alert-danger">
            {{ passwordError }}
          </div>

          <div v-if="passwordSuccess" class="alert alert-success">
            {{ passwordSuccess }}
          </div>

          <form @submit.prevent="changePassword">
            <div class="row my-3">
              <div class="col-12 px-3">
                <div class="input-group">
                  <span class="input-group-text">Current password</span>
                  <input type="password" class="form-control" v-model="currentPassword" autocomplete="current-password">
                </div>
              </div>
            </div>

            <div class="row my-3">
              <div class="col-12 px-3">
                <div class="input-group">
                  <span class="input-group-text">New password</span>
                  <input :type="showGenerated ? 'text' : 'password'" class="form-control" v-model="newPassword" autocomplete="new-password">
                  <button type="button" class="btn btn-outline-secondary" @click="fillGeneratedPassword" title="Generate a strong password">
                    Generate
                  </button>
                  <button type="button" class="btn btn-outline-secondary" :disabled="!newPassword" @click="copyGenerated" title="Copy to clipboard">
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <div class="row my-3">
              <div class="col-12 px-3">
                <div class="input-group">
                  <span class="input-group-text">Confirm new password</span>
                  <input :type="showGenerated ? 'text' : 'password'" class="form-control" v-model="confirmPassword" autocomplete="new-password">
                </div>
              </div>
            </div>

            <div class="row my-3">
              <div class="col-12 px-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="revokeOtherSessions" v-model="revokeOtherSessions">
                  <label class="form-check-label ms-2" for="revokeOtherSessions">
                    Sign out other sessions
                  </label>
                </div>
              </div>
            </div>

            <div class="row my-3">
              <div class="col-12 text-center">
                <button type="submit" class="btn btn-purple" :disabled="submitting" style="width:180px;">
                  {{ submitting ? 'Updating…' : 'Update password' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

    </div>
  </div>
</template>

